// Copyright 2020 The Nakama Authors
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ApiStorageObject, StorageObjectRequest, StoragePermission, StorageWritePermission } from '../../types/storage';
import { UserRole } from '../../types/auth';
import { consoleService } from '../../services/consoleService';
import { authService } from '../../services/authService';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';
import ConfirmDialog from '../ConfirmDialog';
import UnsavedChangesModal from '../UnsavedChangesModal';
import './StorageObject.scss';

// SVG Icons
const RedTriangleIcon = () => (
  <img src="/static/svg/red-triangle.svg" alt="" width="16" height="16" className="mr-2" />
);

const LoadingSpinner = () => (
  <div className="spinner-border spinner-border-sm" role="status">
    <span className="visually-hidden">Loading...</span>
  </div>
);

const StorageObject: React.FC = () => {
  const { collection, key, userId } = useParams<{
    collection: string;
    key: string;
    userId: string;
  }>();
  const navigate = useNavigate();
  const { dialogState, openConfirmDialog, closeConfirmDialog, handleConfirm } = useConfirmDialog();

  const [storageObject, setStorageObject] = useState<ApiStorageObject | null>(null);
  const [editedValue, setEditedValue] = useState('');
  const [editedJsonFields, setEditedJsonFields] = useState<Record<string, any>>({});
  const [editedPermissionRead, setEditedPermissionRead] = useState(StoragePermission.OWNER_READ);
  const [editedPermissionWrite, setEditedPermissionWrite] = useState(StorageWritePermission.OWNER_WRITE);
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [jsonParseError, setJsonParseError] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const sessionRole = authService.getSessionRole();

  // Track unsaved changes
  const {
    showUnsavedChangesModal,
    isSaving,
    confirmLeave,
    saveAndLeave,
    cancelLeave
  } = useUnsavedChanges({
    hasUnsavedChanges: hasUnsavedChanges && isEditing,
    onSave: async () => {
      await handleSave();
    },
    message: 'You have unsaved changes to the storage object. Are you sure you want to leave without saving?'
  });

  // Parse JSON into individual fields for editing
  const parseJsonToFields = (jsonString: string): Record<string, any> => {
    try {
      if (!jsonString.trim()) return {};
      const parsed = JSON.parse(jsonString);
      return parsed;
    } catch (error) {
      setJsonParseError('Invalid JSON format');
      return {};
    }
  };

  // Reconstruct JSON from form fields
  const reconstructJsonFromFields = (fields: Record<string, any>): string => {
    try {
      return JSON.stringify(fields, null, 2);
    } catch (error) {
      setJsonParseError('Error reconstructing JSON');
      return '';
    }
  };

  // Add a new field to the JSON
  const addJsonField = () => {
    const newKey = `field_${Object.keys(editedJsonFields).length + 1}`;
    setEditedJsonFields(prev => ({
      ...prev,
      [newKey]: ''
    }));
  };

  // Remove a field from the JSON
  const removeJsonField = (key: string) => {
    setEditedJsonFields(prev => {
      const newFields = { ...prev };
      delete newFields[key];
      return newFields;
    });
  };

  // Update a field value
  const updateJsonField = (key: string, value: any) => {
    setEditedJsonFields(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Update field key (rename)
  const updateJsonFieldKey = (oldKey: string, newKey: string) => {
    if (oldKey === newKey) return;
    
    setEditedJsonFields(prev => {
      const newFields = { ...prev };
      const value = newFields[oldKey];
      delete newFields[oldKey];
      newFields[newKey] = value;
      return newFields;
    });
  };

  useEffect(() => {
    if (collection && key && userId) {
      loadStorageObject();
    }
  }, [collection, key, userId]);

  // Check for changes when editing
  useEffect(() => {
    if (!storageObject || !isEditing) {
      setHasUnsavedChanges(false);
      return;
    }

    const originalJsonFields = parseJsonToFields(storageObject.value || '{}');
    const hasFieldChanges = JSON.stringify(editedJsonFields) !== JSON.stringify(originalJsonFields);
    const hasPermissionChanges = 
      editedPermissionRead !== (storageObject.permission_read || StoragePermission.OWNER_READ) ||
      editedPermissionWrite !== (storageObject.permission_write || StorageWritePermission.OWNER_WRITE);

    setHasUnsavedChanges(hasFieldChanges || hasPermissionChanges);
  }, [editedJsonFields, editedPermissionRead, editedPermissionWrite, storageObject, isEditing]);

  const loadStorageObject = async () => {
    if (!collection || !key || !userId) return;

    setLoading(true);
    setError('');

    try {
      const decodedCollection = decodeURIComponent(collection);
      const decodedKey = decodeURIComponent(key);
      const decodedUserId = decodeURIComponent(userId);

      const result = await consoleService.getStorageObject('', decodedCollection, decodedKey, decodedUserId);
      setStorageObject(result);
      setEditedValue(result.value || '');
      setEditedJsonFields(parseJsonToFields(result.value || '{}'));
      setEditedPermissionRead(result.permission_read || StoragePermission.OWNER_READ);
      setEditedPermissionWrite(result.permission_write || StorageWritePermission.OWNER_WRITE);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load storage object');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!storageObject) return;

    setSaving(true);
    setError('');
    setJsonParseError('');

    try {
      // Reconstruct JSON from form fields
      const reconstructedJson = reconstructJsonFromFields(editedJsonFields);
      if (jsonParseError) {
        setError('Invalid JSON format. Please check your field values.');
        return;
      }

      const request: StorageObjectRequest = {
        collection: storageObject.collection,
        key: storageObject.key,
        user_id: storageObject.user_id,
        value: reconstructedJson,
        version: storageObject.version,
        permission_read: editedPermissionRead,
        permission_write: editedPermissionWrite,
      };

      await consoleService.writeStorageObject('', request);
      
      // Reload the object to get the updated version
      await loadStorageObject();
      setIsEditing(false);
      setHasUnsavedChanges(false);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to save storage object';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = () => {
    if (!storageObject) return;

    openConfirmDialog(
      'Delete Storage Object',
      `Are you sure you want to delete the storage object "${storageObject.collection}/${storageObject.key}" for user "${storageObject.user_id}"? This action cannot be undone.`,
      async () => {
        try {
          await consoleService.deleteStorageObjects('', {
            object_ids: [{
              collection: storageObject.collection,
              key: storageObject.key,
              user_id: storageObject.user_id,
              version: storageObject.version
            }]
          });
          navigate('/storage');
        } catch (err: any) {
          setError(err.response?.data?.message || err.message || 'Failed to delete storage object');
        }
      }
    );
  };

  const handleCancel = () => {
    if (storageObject) {
      setEditedValue(storageObject.value || '');
      setEditedJsonFields(parseJsonToFields(storageObject.value || '{}'));
      setEditedPermissionRead(storageObject.permission_read || StoragePermission.OWNER_READ);
      setEditedPermissionWrite(storageObject.permission_write || StorageWritePermission.OWNER_WRITE);
    }
    setJsonParseError('');
    setIsEditing(false);
    setHasUnsavedChanges(false);
  };

  const canEdit = () => {
    return sessionRole === UserRole.USER_ROLE_ADMIN;
  };

  const canDelete = () => {
    return sessionRole === UserRole.USER_ROLE_ADMIN;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString();
  };

  const getPermissionText = (permission: number, isWrite: boolean = false) => {
    if (isWrite) {
      switch (permission) {
        case StorageWritePermission.NO_WRITE:
          return 'No Write';
        case StorageWritePermission.OWNER_WRITE:
          return 'Owner Write';
        default:
          return 'Unknown';
      }
    } else {
      switch (permission) {
        case StoragePermission.NO_READ:
          return 'No Read';
        case StoragePermission.OWNER_READ:
          return 'Owner Read';
        case StoragePermission.PUBLIC_READ:
          return 'Public Read';
        default:
          return 'Unknown';
      }
    }
  };

  const formatJsonValue = (value: string) => {
    try {
      const parsed = JSON.parse(value);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return value;
    }
  };

  // Determine input type for a field value
  const getInputType = (value: any): string => {
    if (typeof value === 'boolean') return 'checkbox';
    if (typeof value === 'number') return 'number';
    if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) return 'datetime-local';
    return 'text';
  };

  // Convert value for input display
  const getInputValue = (value: any, type: string): string | boolean => {
    if (type === 'checkbox') return Boolean(value);
    if (type === 'datetime-local' && typeof value === 'string') {
      return value.substring(0, 16); // Convert ISO string to datetime-local format
    }
    return String(value);
  };

  // Convert input value back to appropriate type
  const convertInputValue = (value: string | boolean, originalValue: any): any => {
    if (typeof originalValue === 'boolean') return Boolean(value);
    if (typeof originalValue === 'number') return Number(value);
    if (typeof originalValue === 'string' && originalValue.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
      return String(value) + 'Z'; // Add timezone back
    }
    return String(value);
  };

  if (loading) {
    return (
      <div className="storage-object-container">
        <div className="alert alert-info">
          <LoadingSpinner />
          <strong className="ms-2">Loading storage object...</strong>
        </div>
      </div>
    );
  }

  if (!storageObject) {
    return (
      <div className="storage-object-container">
        <div className="alert alert-danger">
          <RedTriangleIcon />
          <strong>Storage object not found</strong>
        </div>
      </div>
    );
  }

  return (
    <div className="storage-object-container">
      <div className="storage-object-header">
        <div className="header-content">
          <h2 className="storage-object-title">Storage Object</h2>
          <div className="storage-object-path">
            <span className="collection">{storageObject.collection}</span>
            <span className="separator">/</span>
            <span className="key">{storageObject.key}</span>
          </div>
        </div>
        <div className="header-actions">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => navigate('/storage')}
          >
            Back to Storage
          </button>
          {canEdit() && !isEditing && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setIsEditing(true)}
            >
              Edit
            </button>
          )}
          {canDelete() && !isEditing && (
            <button
              type="button"
              className="btn btn-danger"
              onClick={handleDelete}
            >
              Delete
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="alert alert-danger">
          <RedTriangleIcon />
          <strong>Error:</strong> {error}
        </div>
      )}

      <div className="storage-object-content">
        <div className="metadata-section">
          <h3>Metadata</h3>
          <div className="metadata-grid">
            <div className="metadata-item">
              <label>Collection:</label>
              <span>{storageObject.collection}</span>
            </div>
            <div className="metadata-item">
              <label>Key:</label>
              <span>{storageObject.key}</span>
            </div>
            <div className="metadata-item">
              <label>User ID:</label>
              <span>{storageObject.user_id}</span>
            </div>
            <div className="metadata-item">
              <label>Version:</label>
              <span>{storageObject.version}</span>
            </div>
            <div className="metadata-item">
              <label>Read Permission:</label>
              {isEditing ? (
                <select
                  value={editedPermissionRead}
                  onChange={(e) => setEditedPermissionRead(parseInt(e.target.value))}
                  className="form-select"
                >
                  <option value={StoragePermission.NO_READ}>No Read</option>
                  <option value={StoragePermission.OWNER_READ}>Owner Read</option>
                  <option value={StoragePermission.PUBLIC_READ}>Public Read</option>
                </select>
              ) : (
                <span>{getPermissionText(storageObject.permission_read)}</span>
              )}
            </div>
            <div className="metadata-item">
              <label>Write Permission:</label>
              {isEditing ? (
                <select
                  value={editedPermissionWrite}
                  onChange={(e) => setEditedPermissionWrite(parseInt(e.target.value))}
                  className="form-select"
                >
                  <option value={StorageWritePermission.NO_WRITE}>No Write</option>
                  <option value={StorageWritePermission.OWNER_WRITE}>Owner Write</option>
                </select>
              ) : (
                <span>{getPermissionText(storageObject.permission_write, true)}</span>
              )}
            </div>
            <div className="metadata-item">
              <label>Created:</label>
              <span>{formatDate(storageObject.create_time)}</span>
            </div>
            <div className="metadata-item">
              <label>Updated:</label>
              <span>{formatDate(storageObject.update_time)}</span>
            </div>
          </div>
        </div>

        <div className="value-section">
          <h3>Value</h3>
          {isEditing ? (
            <div className="edit-form">
              {jsonParseError && (
                <div className="alert alert-warning">
                  <strong>JSON Parse Error:</strong> {jsonParseError}
                </div>
              )}
              
              <div className="json-fields-container">
                <div className="json-fields-header">
                  <h5>JSON Fields</h5>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    onClick={addJsonField}
                  >
                    + Add Field
                  </button>
                </div>
                
                {Object.keys(editedJsonFields).length === 0 ? (
                  <div className="no-fields-message">
                    <p>No fields found. Click "Add Field" to start editing.</p>
                  </div>
                ) : (
                  <div className="json-fields-list">
                    {Object.entries(editedJsonFields).map(([key, value]) => {
                      const inputType = getInputType(value);
                      const inputValue = getInputValue(value, inputType);
                      
                      return (
                        <div key={key} className="json-field-row">
                          <div className="field-key">
                            <input
                              type="text"
                              value={key}
                              onChange={(e) => updateJsonFieldKey(key, e.target.value)}
                              className="form-control"
                              placeholder="Field name"
                            />
                          </div>
                          <div className="field-value">
                            {inputType === 'checkbox' ? (
                              <div className="form-check">
                                <input
                                  type="checkbox"
                                  checked={Boolean(inputValue)}
                                  onChange={(e) => updateJsonField(key, e.target.checked)}
                                  className="form-check-input"
                                />
                                <label className="form-check-label">
                                  {Boolean(inputValue) ? 'True' : 'False'}
                                </label>
                              </div>
                            ) : (
                              <input
                                type={inputType}
                                value={inputValue as string}
                                onChange={(e) => {
                                  const convertedValue = convertInputValue(e.target.value, value);
                                  updateJsonField(key, convertedValue);
                                }}
                                className="form-control"
                                placeholder="Field value"
                              />
                            )}
                          </div>
                          <div className="field-actions">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => removeJsonField(key)}
                              title="Remove field"
                            >
                              ×
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              
              <div className="edit-actions">
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? <LoadingSpinner /> : 'Save'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCancel}
                  disabled={saving}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <pre className="value-display">
              {formatJsonValue(storageObject.value || '')}
            </pre>
          )}
        </div>
      </div>

      <ConfirmDialog
        isOpen={dialogState.isOpen}
        title={dialogState.title}
        message={dialogState.message}
        onConfirm={handleConfirm}
        onCancel={closeConfirmDialog}
      />

      <UnsavedChangesModal
        isOpen={showUnsavedChangesModal}
        message="You have unsaved changes to the storage object. Are you sure you want to leave without saving?"
        onSaveAndLeave={saveAndLeave}
        onLeaveWithoutSaving={confirmLeave}
        onCancel={cancelLeave}
        isSaving={isSaving}
        showSaveOption={canEdit()}
      />
    </div>
  );
};

export default StorageObject;
